import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, IconButton, Snackbar, Alert, Tooltip, Card, Avatar, CardContent, Checkbox, useMediaQuery, useTheme, CircularProgress, LinearProgress, TextField, InputAdornment, Select, MenuItem, FormControl, Dialog, Button, DialogContent, Chip, Divider, } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { initPullToRefresh } from '../utils/pullToRefresh';
import { VscNewFile } from "react-icons/vsc";
import { FaFileExcel } from 'react-icons/fa';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuth } from '../context/AuthContext';
import useDataLoadingState from '../hooks/useDataLoadingState';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import CommonServerDataGrid from '../components/datagridComponent';
import { formatRows } from '../utils/formatRows';
import NoDataComponent from '../components/NoDataComponent';
import CenterLoader from './Test/components/CenterLoader';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import { APPBAR_HEIGHT, colors, gradients } from '../utils/theme';
import LoadingOverlay from '../components/LoadingOverlay';
import SkeletonLoader from '../components/SkeletonLoader';
import { motion } from 'framer-motion';

/**
 * Utility: Convert string to color
 */
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4'];
    return colors[Math.abs(hash) % colors.length];
}

/**
 * Utility: Get initials from name
 */
function getInitials(name) {
    if (!name) return 'C';
    return name.trim().charAt(0).toUpperCase();
}

/**
 * ClientDetail2 Component
 * 
 * Displays clients in two views:
 * - Desktop: CommonServerDataGrid with pagination, filtering, sorting
 * - Mobile: Card view with search and pagination
 * 
 * Features:
 * - Server-side pagination, filtering, sorting
 * - Search functionality for mobile view
 * - Search field selector for desktop view
 * - Bulk deletion
 * - Excel export
 * - Responsive design
 */
export default function ClientDetail2({ connectionString }) {
    const navigate = useNavigate();
    const { user, selectedCompany } = useAuth();
    const API = `${connectionString}/client`;
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));
    const [isFiltered, setIsFiltered] = useState(false);

    // ============ STATE MANAGEMENT ============

    // Shared state for both views
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [selectAll, setSelectAll] = useState(false);

    // Desktop state (DataGrid)
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [sortModel, setSortModel] = useState([]);
    const [searchField, setSearchField] = useState('FirmName'); // Default to Company Name

    // Mobile state (Search + Pagination)
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [debouncedMobileSearch, setDebouncedMobileSearch] = useState('');
    const [mobilePaginationModel, setMobilePaginationModel] = useState({ page: 0, pageSize: 20 });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const selectedIdsRef = useRef([]);
    // Guards against a slow/stale fetch (e.g. Slow 4G) resolving after a newer
    // one has started and clobbering fresher rows/loading state.
    const fetchRequestIdRef = useRef(0);

    // 🔥 Add loading state hook
    const { showLoadingSpinner, showEmptyState } = useDataLoadingState(loading, rows?.length > 0);

    console.log('=== COMPONENT RENDER ===');
    console.log('isSmallScreen:', isSmallScreen);
    console.log('mobileSearchQuery:', mobileSearchQuery);
    console.log('debouncedMobileSearch:', debouncedMobileSearch);
    console.log('rows count:', rows.length);
    console.log('totalCount:', totalCount);

    // ============ PARAMETER BUILDERS ============

    /**
     * Build parameters for mobile search request
     */
    const buildMobileParams = (pageNum, searchText) => {
        const offset = pageNum * 20;
        const params = { mode: 'search', companyId, userId, offset, limit: 20 };

        if (searchText && searchText.trim()) {
            params.search = searchText; // Send search to backend
        }

        console.log('📦 buildMobileParams:', params);
        return params;
    };

    /**
     * Build parameters for desktop request with filters and sort
     */
    const buildDesktopParams = (pageNum, pageSize) => {
        const offset = pageNum * pageSize;
        const params = { mode: 'search', companyId, userId, offset, limit: pageSize };

        // Add filters from DataGrid
        filterModel.items.forEach(item => {
            if (item.value) {
                // Map filter field to database column
                const fieldMap = { 'FirmName': 'FirmName', 'ContactPerson': 'ContactPerson', 'MobileNo': 'MobileNo', 'GSTNo': 'GSTNo', 'City': 'City', 'state': 'state' };
                const dbColumn = fieldMap[item.field] || item.field;
                params[dbColumn] = item.value;
            }
        });

        // Add sorting
        if (sortModel.length > 0) {
            const sort = sortModel[0];
            params.orderBy = `${sort.field} ${sort.sort.toUpperCase()}`;
        }

        console.log('🖥️ buildDesktopParams:', params);
        return params;
    };

    // ============ UNIFIED PARAMETER BUILDER ============

    // Stable key for filterModel.items that only changes when an actual filter
    // VALUE changes. Opening the DataGrid filter panel (and picking a field or
    // operator before typing anything) adds/changes a blank filter item — a new
    // object reference with field/operator set but value empty — which would
    // otherwise retrigger a fetch. On a slow connection that fetch's loading
    // state surfaces mid-interaction and looks like the grid "reloaded" just
    // from opening the filter. Items with no value are excluded entirely so
    // only a real, typed value changes the key.
    const filterKey = filterModel.items.filter(i => i.value !== undefined && i.value !== null && String(i.value).trim() !== '').map(i => `${i.field}:${i.operator}:${i.value}`).join('|');

    const buildParams = useCallback(() => {
        if (isSmallScreen) {
            // Mobile params
            const offset = mobilePaginationModel.page * mobilePaginationModel.pageSize;
            const params = { mode: 'search', companyId, userId, offset, limit: mobilePaginationModel.pageSize, };
            if (debouncedMobileSearch?.trim()) {
                params.search = debouncedMobileSearch;
            }
            return params;
        } else {
            // Desktop params
            const offset = paginationModel.page * paginationModel.pageSize;
            const params = { mode: 'search', companyId, userId, offset, limit: paginationModel.pageSize, };
            filterModel.items.forEach(item => {
                if (item.value) {
                    const fieldMap = { FirmName: 'FirmName', ContactPerson: 'ContactPerson', MobileNo: 'MobileNo', GSTNo: 'GSTNo', City: 'City', state: 'state', };
                    params[fieldMap[item.field] || item.field] = item.value;
                }
            });
            if (sortModel.length > 0) {
                const sort = sortModel[0];
                params.orderBy = `${sort.field} ${sort.sort.toUpperCase()}`;
            }
            return params;
        }
    }, [ isSmallScreen, companyId, userId, paginationModel, filterKey, sortModel, mobilePaginationModel, debouncedMobileSearch, ]);

    // ============ SINGLE UNIFIED FETCH ============

    const fetchData = useCallback(async () => {
        const requestId = ++fetchRequestIdRef.current;
        setLoading(true);
        try {
            const params = buildParams();

            // Single data call — works for both mobile and desktop
            const [dataRes, countRes] = await Promise.all([
                axios.get(`${API}/search`, { params }),
                axios.get(`${API}/count`, {
                    params: (() => {
                        // Count params: same filters, no pagination/sort
                        const cp = { companyId, userId };
                        if (isSmallScreen) {
                            if (debouncedMobileSearch?.trim()) cp.search = debouncedMobileSearch;
                        } else {
                            filterModel.items.forEach(item => {
                                if (item.value) {
                                    const fieldMap = { FirmName: 'FirmName', ContactPerson: 'ContactPerson', MobileNo: 'MobileNo', GSTNo: 'GSTNo', City: 'City', state: 'state', };
                                    cp[fieldMap[item.field] || item.field] = item.value;
                                }
                            });
                        }
                        return cp;
                    })(),
                }),
            ]);

            // A newer fetch started while this one was in flight (e.g. slow
            // network) — drop this stale response instead of overwriting fresher data.
            if (requestId !== fetchRequestIdRef.current) return;

            const fetchedRows = Array.isArray(dataRes.data) ? dataRes.data : [];
            setRows(formatRows(fetchedRows));
            setTotalCount(Number(countRes.data) || 0);
            setSelectionModel([]);
            selectedIdsRef.current = [];

            console.log(`✅ Fetched ${fetchedRows.length} rows | Total: ${Number(countRes.data) || 0} | Mode: ${isSmallScreen ? 'mobile' : 'desktop'}`);
        } catch (err) {
            if (requestId !== fetchRequestIdRef.current) return;
            console.error('❌ Fetch error:', err);
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
            setRows([]);
            setTotalCount(0);
        } finally {
            if (requestId === fetchRequestIdRef.current) setLoading(false);
        }
    }, [API, companyId, userId, buildParams, isSmallScreen, debouncedMobileSearch, filterKey]);

    // ============ FETCH FUNCTIONS ============

    /**
     * Fetch data for desktop view (with filters and sort)
     */
    const fetchDataDesktop = useCallback(async () => {
        if (isSmallScreen) return;

        console.log('🖥️ [DESKTOP] Fetching data...');
        setLoading(true);
        try {
            const params = buildDesktopParams(paginationModel.page, paginationModel.pageSize);

            // Fetch rows
            const dataRes = await axios.get(`${API}/search`, { params });
            const fetchedRows = Array.isArray(dataRes.data) ? dataRes.data : [];
            setRows(formatRows(fetchedRows));
            setSelectionModel([]);
            selectedIdsRef.current = [];
            console.log(`🖥️ Fetched ${fetchedRows.length} rows`);

            // Fetch total count
            const countParams = { companyId, userId };

            // Add same filters as search
            filterModel.items.forEach(item => {
                if (item.value) {
                    const fieldMap = { 'FirmName': 'FirmName', 'ContactPerson': 'ContactPerson', 'MobileNo': 'MobileNo', 'GSTNo': 'GSTNo', 'City': 'City', 'state': 'state' };
                    const dbColumn = fieldMap[item.field] || item.field;
                    countParams[dbColumn] = item.value;
                }
            });

            const countRes = await axios.get(`${API}/count`, { params: countParams });
            const count = Number(countRes.data) || 0;
            setTotalCount(count);

            console.log(`🖥️ Total count: ${count}`);
        } catch (err) {
            console.error('🖥️ Fetch error:', err);
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [API, companyId, userId, paginationModel, filterModel, sortModel, isSmallScreen]);

    /**
     * Fetch data for mobile view (with search only)
     */
    const fetchDataMobile = useCallback(async () => {
        console.log('📱 [MOBILE] Fetching data...');

        if (!isSmallScreen) {
            console.log('📱 Skipping - not small screen');
            return;
        }

        setLoading(true);
        try {
            const params = buildMobileParams(mobilePaginationModel.page, debouncedMobileSearch);

            console.log('📱 API URL:', `${API}/search`);
            console.log('📱 Params:', JSON.stringify(params, null, 2));

            // Fetch rows
            const dataRes = await axios.get(`${API}/search`, { params });
            const fetchedRows = Array.isArray(dataRes.data) ? dataRes.data : [];
            setRows(formatRows(fetchedRows));
            setSelectionModel([]);
            selectedIdsRef.current = [];
            console.log(`📱 Fetched ${fetchedRows.length} rows`);

            // Fetch total count
            const countParams = { companyId, userId };
            if (debouncedMobileSearch && debouncedMobileSearch.trim()) {
                countParams.search = debouncedMobileSearch;
            }

            console.log('📱 Count params:', countParams);
            const countRes = await axios.get(`${API}/count`, { params: countParams });
            const count = Number(countRes.data) || 0;
            setTotalCount(count);

            console.log(`📱 Total count: ${count}`);
        } catch (err) {
            console.error('📱 Fetch error:', err);
            console.error('📱 Error response:', err.response?.data);
            setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [API, companyId, userId, mobilePaginationModel, debouncedMobileSearch, isSmallScreen]);

    // ============ EFFECTS ============
    // In your React web app - temporary debug helper
    useEffect(() => {
        const originalPostMessage = window.ReactNativeWebView?.postMessage;

        // ✅ Log every message sent TO React Native
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage = (data) => {
                console.log('📤 Sent to RN:', JSON.parse(data));
                originalPostMessage.call(window.ReactNativeWebView, data);
            };
        }

        // ✅ Log every message received FROM React Native
        const handleMessage = (e) => {
            try {
                const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                console.log('📥 Received from RN:', data);
            } catch { }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // ============ EFFECTS ============

    // Single effect — triggers for both mobile and desktop dependencies
    useEffect(() => {
        fetchData();
    }, [ paginationModel.page, paginationModel.pageSize, filterKey, sortModel, mobilePaginationModel.page, mobilePaginationModel.pageSize, debouncedMobileSearch, isSmallScreen, fetchData, ]);

    // Debounce mobile search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedMobileSearch(mobileSearchQuery);
            setMobilePaginationModel({ page: 0, pageSize: 20 });
        }, 400);
        return () => clearTimeout(timer);
    }, [mobileSearchQuery]);

    /**
     * Trigger desktop fetch when pagination, filters, or sort changes
     */
    // useEffect(() => {
    //     console.log('💻 [EFFECT] Desktop dependencies changed');
    //     if (!isSmallScreen) {
    //         fetchDataDesktop();
    //     }
    // }, [paginationModel.page, paginationModel.pageSize, filterModel, sortModel, isSmallScreen, fetchDataDesktop]);

    // /**
    //  * Trigger mobile fetch when pagination changes
    //  */
    // useEffect(() => {
    //     console.log('📱 [EFFECT] Mobile pagination changed:', mobilePaginationModel);
    //     if (isSmallScreen) {
    //         fetchDataMobile();
    //     }
    // }, [mobilePaginationModel.page, mobilePaginationModel.pageSize, isSmallScreen, debouncedMobileSearch]);

    // /**
    //  * Debounce mobile search and trigger fetch
    //  */
    // useEffect(() => {
    //     console.log('⏱️ [DEBOUNCE] Starting 400ms timeout for:', mobileSearchQuery);

    //     const timer = setTimeout(() => {
    //         console.log('⏱️ [DEBOUNCE] Timer completed! Setting search to:', mobileSearchQuery);
    //         setDebouncedMobileSearch(mobileSearchQuery);
    //         setMobilePaginationModel({ page: 0, pageSize: 20 });
    //     }, 400);

    //     return () => {
    //         console.log('⏱️ [DEBOUNCE] Cleared timeout');
    //         clearTimeout(timer);
    //     };
    // }, [mobileSearchQuery]);

    /**
     * Trigger fetch when debounced search changes
     */
    // useEffect(() => {
    //     console.log('📱 [EFFECT] Debounced search changed:', debouncedMobileSearch);
    //     if (isSmallScreen) {
    //         fetchDataMobile();
    //     }
    // }, [debouncedMobileSearch, isSmallScreen, fetchDataMobile]);

    // ============ DELETE HANDLER ============

    const handleDelete = async () => {
        // if (selectionModel.length === 0) return;

        const ids = selectedIdsRef.current;
        if (!ids.length || deleting) return;

        setDeleting(true);
        try {
            const response = await axios.post(`${API}/delete`, { userId, objAryId: ids });
            setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
            setSelectionModel([]);
            selectedIdsRef.current = [];
            setSelectAll(false);

            // Refetch data
            if (isSmallScreen) {
                // fetchDataMobile();
                fetchData();
            } else {
                // fetchDataDesktop();
                fetchData();
            }
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            // 🔴 Case: 400 + referenced IDs (business rule failure)
            if (status === 400 && data?.referencedIds?.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else if (status === 400 && data?.error) {
                // 🔴 400 but no referencedIds array (generic validation)
                setSnackbar({ open: true, message: data.error, severity: "warning" });
            } else {
                // 🔴 unexpected / server error
                setSnackbar({ open: true, message: "Delete failed due to an unexpected error.", severity: "error" });
            }
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    // ============ EXPORT HANDLER ============

    const funExportData = () => {
        axios({ url: `${API}/search?mode=export&companyId=${companyId}&userId=${userId}`, method: 'GET', responseType: 'blob', })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'exportedClients.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((error) => {
                console.error('Export error:', error);
                setSnackbar({ open: true, message: 'Export failed.', severity: 'error' });
            });
    };

    // ============ SELECT ALL TOGGLE ============

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectionModel([]);
            selectedIdsRef.current = [];
            setSelectAll(false);
        } else {
            const allIds = rows.map(row => row.id);
            setSelectionModel(allIds);
            selectedIdsRef.current = allIds; // 🔥 freeze
            setSelectAll(true);

        }
    };

    // ============ COLUMNS DEFINITION ============

    const columns = [
        {
            field: 'FirmName', headerName: 'FirmName', flex: 1, minWidth: 150,
            renderCell: (params) => {
                const handleClick = (e) => {
                    if (role === 'User') {
                        e.preventDefault();
                        setSnackbar({ open: true, message: '403 Forbidden: Access Denied', severity: 'warning' });
                    } else {
                        navigate(`/main/editClient/${params.row.id}`);
                    }
                };
                return (
                    <Tooltip title={params.value || ''} placement="top">
                        <Typography onClick={handleClick} sx={{ cursor: 'pointer', color: colors.primary.main, textDecoration: 'underline', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {params.value}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: 'ContactPerson', headerName: 'Contact Person', flex: 0.8, minWidth: 120,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} placement="top">
                    <Typography sx={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {params.value || '—'}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: 'MobileNo', headerName: 'Mobile No.', flex: 0.4, minWidth: 120,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {params.value || '—'}
                </Typography>
            )
        },
        {
            field: 'GSTNo', headerName: 'GST No.', flex: 0.45, minWidth: 120,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} placement="top">
                    <Typography sx={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {params.value || '—'}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: 'location', headerName: 'Location', flex: 0.45, minWidth: 120, sortable: false, filterable: true,
            valueGetter: (value, row) => [row.City, row.state].filter(Boolean).join(', '),
            renderCell: (params) => {
                const { City, state } = params.row;
                return (
                    <Box sx={{ overflow: 'hidden', width: '100%' }}>
                        <Typography sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{City || '—'}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state || '—'}</Typography>
                    </Box>
                );
            }
        },
    ];

    // Add delete button column if not User
    if (role !== "User") {
        columns.push({
            flex: 0.15, sortable: false, filterable: false, headerAlign: 'right', align: 'right',
            renderCell: (params) => {
                const isRowSelected = selectionModel.includes(params.row.id);
                return (
                    <Tooltip title={isRowSelected ? 'Delete Selected' : 'Select row to enable'}>
                        <span>
                            <IconButton color="error" disabled={!isRowSelected} onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                selectedIdsRef.current = selectionModel;   // 🔥 SYNC HERE
                                setPendingDeleteIds(selectionModel);
                                setConfirmOpen(true);
                            }} >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        });
    }

    // ============ CARD RENDERING (MOBILE) ============

    const renderCards = () => {
        if (showLoadingSpinner) {
            return <SkeletonLoader count={3} height={100} />;
        }

        if (showEmptyState) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                        {mobileSearchQuery ? <CustomNoDataOverlay /> : <NoDataComponent />}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Search: "{debouncedMobileSearch}" | Page: {mobilePaginationModel.page} | Total: {totalCount}
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                {rows.map((row) => {
                    const isSelected = selectionModel.includes(row.id);
                    const avatarColor = stringToColor(row.FirmName || 'Client');
                    const initials = getInitials(row.FirmName);
                    const locationText = [row.City, row.state].filter(Boolean).join(', ') || '—';

                    const toggleSelection = (e) => {
                        e.stopPropagation();
                        setSelectionModel(prev => {
                            const updated = prev.includes(row.id) ? prev.filter(id => id !== row.id) : [...prev, row.id];

                            selectedIdsRef.current = updated; // 🔥 freeze selection
                            return updated;
                        });
                    };

                    return (
                        <Card key={row.id} sx={{ mb: 1.5, boxShadow: 1, borderRadius: 2, overflow: 'hidden', transition: '0.2s', '&:hover': { boxShadow: 3 }, cursor: 'pointer' }}
                            onClick={() => {
                                if (role === 'User') {
                                    setSnackbar({ open: true, message: '403 Forbidden: Access Denied', severity: 'warning' });
                                    return;
                                }
                                navigate(`/main/editClient/${row.id}`);
                            }}
                        >
                            {/* Gradient top strip */}
                            <Box sx={{ height: 5, background: `linear-gradient(90deg, ${avatarColor} 0%, ${avatarColor}88 100%)` }} />

                            <CardContent sx={{ py: 1.5, px: 1.5, pb: '10px !important' }}>
                                {/* Header row: Checkbox + Avatar + Firm Name + Location */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                    {role !== 'User' && (
                                        <Checkbox checked={isSelected} onChange={toggleSelection} onClick={(e) => e.stopPropagation()} size="small" sx={{ p: 0.2, mt: 0.3, mr: 0.5, flexShrink: 0 }} />
                                    )}
                                    <Avatar sx={{ bgcolor: avatarColor, color: themeColors.neutral.surface, width: 38, height: 38, fontWeight: 'bold', mr: 1.5, flexShrink: 0 }} >
                                        {initials}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="subtitle1" sx={{ color: colors.primary.main, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} >
                                            {row.FirmName || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                            {locationText}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 0.8 }} />

                                {/* Info area */}
                                <Box sx={{ mt: 0.8 }}>
                                    {row.ContactPerson && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                                            <PersonIcon sx={{ color: 'text.secondary', mr: 0.8, flexShrink: 0, fontSize: '0.95rem' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}> {row.ContactPerson} </Typography>
                                        </Box>
                                    )}

                                    {/* Chip badges */}
                                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.5 }}>
                                        {row.GSTNo && ( <Chip icon={<ReceiptIcon />} label={`GST: ${row.GSTNo}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} /> )}
                                        {row.MobileNo && ( <Chip icon={<PhoneIcon />} label={row.MobileNo} size="small" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} /> )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}

                {/* Mobile Pagination Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2, mt: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<NavigateBeforeIcon />} onClick={() => setMobilePaginationModel(p => ({ ...p, page: Math.max(0, p.page - 1) }))} disabled={mobilePaginationModel.page === 0} >
                        Prev
                    </Button>
                    <Typography variant="body2" sx={{ minWidth: '120px', textAlign: 'center' }}>
                        Page {mobilePaginationModel.page + 1} of {Math.ceil(totalCount / mobilePaginationModel.pageSize) || 1}
                    </Typography>
                    <Button variant="outlined" size="small" endIcon={<NavigateNextIcon />} onClick={() => setMobilePaginationModel(p => ({ ...p, page: p.page + 1 }))} disabled={(mobilePaginationModel.page + 1) * mobilePaginationModel.pageSize >= totalCount} >
                        Next
                    </Button>
                </Box>
            </>
        );
    };

    // ============ RENDER ============

    const hasActiveFilter = (filterModel?.items || []).some((f) => f.value !== undefined && f.value !== null && String(f.value).trim() !== "");

    return (
        <>
            <LoadingOverlay isLoading={loading} message="Loading data..." />
            <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 }, display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APPBAR_HEIGHT}px)`, }}>

            {/* ── Module Header ──────────────────────────────────────────────
                Blue gradient strip + Paper bar gives a consistent professional
                look. Title is left-aligned; action buttons sit on the right.
            ────────────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 1.5 }}>
                {/* Thin blue gradient accent strip */}
                <Box sx={{ height: 4, background: gradients.primaryRow, borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Accounts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Export to Excel */}
                        <Tooltip title="Export to Excel">
                            <Button startIcon={<FaFileExcel />} variant="outlined" onClick={funExportData} color="success" size="small">
                                Export
                            </Button>
                        </Tooltip>
                        {/* Navigate to the Add New Client form */}
                        <Button startIcon={<AddIcon />} variant="contained" size="small" component={RouterLink} to="/main/newClient">
                            Add Account
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Content */}
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: "12px" }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 }, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Mobile Search Field */}
                    {isSmallScreen && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder="Search clients..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)} variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    )}

                    {/* Bulk Action Bar - Mobile Only */}
                    {isSmallScreen && role !== 'User' && selectionModel.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }} >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox checked={selectAll} onChange={toggleSelectAll} size="small" />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {selectionModel.length} selected
                                </Typography>
                            </Box>
                            <Tooltip title="Delete selected">
                                <IconButton onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    selectedIdsRef.current = selectionModel;   // 🔥 SYNC HERE
                                    setPendingDeleteIds(selectionModel);
                                    setConfirmOpen(true);

                                }}

                                    color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Mobile View - Cards */}
                    {isSmallScreen ? (
                        <Box sx={{ width: '100%', overflowX: 'hidden', px: 1, flex: 1, overflowY: 'auto'}}>
                            {renderCards()}
                        </Box>
                    ) : (
                        /* Desktop View - CommonServerDataGrid */
                       <Box sx={{ flex: 1, width: '100%', minHeight: 0, position: 'relative' }}>
                            <LoadingOverlay isVisible={showLoadingSpinner} />
                            {showEmptyState && !isFiltered && !hasActiveFilter && <NoDataComponent />}
                            {!(showEmptyState && !isFiltered && !hasActiveFilter) && ( <CommonServerDataGrid data={rows} columns={columns} getRowId={(row) => row.id} role={role} pageSizeOptions={[50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }} selectionModel={selectionModel} onSelectionModelChange={setSelectionModel} onPageChange={({ page, pageSize }) => { setPaginationModel({ page, pageSize }); }} onFilterChange={setFilterModel} onSortChange={setSortModel} rowCount={totalCount} onFilterStatusChange={setIsFiltered} disableEmptyNull /> )}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ──────────────────────────────
                Red gradient strip signals danger. Shows how many records will
                be permanently deleted before the user confirms.
            ────────────────────────────────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => { if (!deleting) setConfirmOpen(false); }} maxWidth="xs" fullWidth>
                {deleting ? <LinearProgress color="error" sx={{ height: 4 }} /> : <Box sx={{ height: 4, background: gradients.danger }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingDeleteIds.length}</Typography>
                        {' '}account record(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting} startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />} onClick={handleDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>

        </Box>
        </>
    );
}